/* eslint-disable hh/comment-budget */
import { createSelector } from '@reduxjs/toolkit'

import { getTenantSettingsSlice } from 'api/tenant-settings/tenantSettingsCrud'
import {
  INVENTORY,
  ROUTE_MANAGER_AUTO_POLLING,
  PAYMENTS_BATCH,
  VISION,
  VISION_REVIEW,
  MESSAGING,
  HERO_ANALYTICS,
  NEW_TAXES_ENABLED,
  LEGACY_TAXES_ENABLED,
  INVENTORY_PLACEMENTS,
  ACCOUNT_LIFECYCLE_CAPTURE,
  OPTIMIZE_STOPS,
  ROUTE_OPTIMIZATION_ASYNC,
  ROUTE_SERVICE_TIME_WINDOWS,
  DIALSTACK_SCREEN_POP,
} from 'settings/constants/tenantSettings'

type TenantSettings = Record<string, boolean>

export const selectTenantSettings = createSelector(
  getTenantSettingsSlice,
  (response: { data?: { stringValues?: Record<string, string> } }) => {
    const stringValues = response?.data?.stringValues ?? {}
    return Object.fromEntries(Object.entries(stringValues).map(([key, value]) => [key, value === 'true']))
  }
)

export const getIsInventoryFeatureEnabled = (tenantSettings: TenantSettings) => tenantSettings?.[INVENTORY] ?? false

export const selectIsInventoryFeatureEnabled = createSelector(selectTenantSettings, getIsInventoryFeatureEnabled)

export const getIsInventoryPlacementsEnabled = (tenantSettings: TenantSettings) => tenantSettings?.[INVENTORY_PLACEMENTS] ?? false

export const selectIsInventoryPlacementsEnabled = createSelector(selectTenantSettings, getIsInventoryPlacementsEnabled)

export const selectIsRouteManagerAutoPollingEnabled = createSelector(
  selectTenantSettings,
  (tenantSettings: TenantSettings) => tenantSettings?.[ROUTE_MANAGER_AUTO_POLLING] ?? false
)

export const selectIsPaymentsBatchEnabled = createSelector(
  selectTenantSettings,
  (tenantSettings: TenantSettings) => tenantSettings?.[PAYMENTS_BATCH] ?? false
)

export const getIsVisionEnabled = (tenantSettings: TenantSettings) => tenantSettings?.[VISION] ?? false

export const selectIsVisionEnabled = createSelector(selectTenantSettings, getIsVisionEnabled)

// `visionReview` is an INDEPENDENT tenant flag from `vision` — the exceptions /
// Review tab gates on this one; `vision` stays for the broader Vision feature.
export const getIsVisionReviewEnabled = (tenantSettings: TenantSettings) => tenantSettings?.[VISION_REVIEW] ?? false

export const selectIsVisionReviewEnabled = createSelector(selectTenantSettings, getIsVisionReviewEnabled)

export const getIsMessagingFeatureEnabled = (tenantSettings: TenantSettings) => tenantSettings?.[MESSAGING] ?? false

export const selectIsMessagingFeatureEnabled = createSelector(selectTenantSettings, getIsMessagingFeatureEnabled)

export const getIsAccountLifecycleCaptureEnabled = (tenantSettings: TenantSettings) => tenantSettings?.[ACCOUNT_LIFECYCLE_CAPTURE] ?? false

export const selectIsAccountLifecycleCaptureEnabled = createSelector(selectTenantSettings, getIsAccountLifecycleCaptureEnabled)

// Hero Analytics (Hauler Hero AI Reports): gated by the heroAnalytics tenant setting (default off).
export const getIsHeroAnalyticsEnabled = (tenantSettings: TenantSettings) => tenantSettings?.[HERO_ANALYTICS] ?? false

export const selectIsHeroAnalyticsEnabled = createSelector(selectTenantSettings, getIsHeroAnalyticsEnabled)

// newTaxesEnabled — show the new-tax UI (nav items, redesigned pages) and rename
// legacy tax UI labels to "Legacy". Default off. Turned on first in the migration
// sequence so CS can see and set up the new taxes while legacy invoicing still runs.
export const getIsNewTaxesEnabled = (tenantSettings: TenantSettings) => tenantSettings?.[NEW_TAXES_ENABLED] ?? false

export const selectIsNewTaxesEnabled = createSelector(selectTenantSettings, getIsNewTaxesEnabled)

// legacyTaxesEnabled — controls whether legacy tax UI sections are visible.
// Defaults to true (show). Turn off at the tail of the migration sequence, after
// attachNewTaxesToInvoices has flipped and the customer's first new-engine invoice
// has been validated — this hides the legacy pages/accordions/pickers so users
// don't confuse them with the new UI. The engine cutover itself is driven by the
// backend-only attachNewTaxesToInvoices flag, not this one.
export const getIsLegacyTaxesEnabled = (tenantSettings: TenantSettings) => tenantSettings?.[LEGACY_TAXES_ENABLED] ?? true

export const selectIsLegacyTaxesEnabled = createSelector(selectTenantSettings, getIsLegacyTaxesEnabled)

export const selectIsOptimizeStopsEnabled = createSelector(
  selectTenantSettings,
  (tenantSettings: TenantSettings) => tenantSettings?.[OPTIMIZE_STOPS] ?? false
)

// Nothing behind it persists until the backend carries the fields, so it stays off until switched over.
export const selectIsRouteServiceTimeWindowsEnabled = createSelector(
  selectTenantSettings,
  (tenantSettings: TenantSettings) => tenantSettings?.[ROUTE_SERVICE_TIME_WINDOWS] ?? false
)

// Also needs the bridge URL, which only the preview build carries.
export const selectIsDialstackScreenPopEnabled = createSelector(
  selectTenantSettings,
  (tenantSettings: TenantSettings) => tenantSettings?.[DIALSTACK_SCREEN_POP] ?? false
)

// Which optimizer a tenant gets; off keeps the legacy bulk-actions flow, so `optimizeStops` is unchanged in meaning.
export const selectIsAsyncRouteOptimizationEnabled = createSelector(
  selectTenantSettings,
  (tenantSettings: TenantSettings) => tenantSettings?.[ROUTE_OPTIMIZATION_ASYNC] ?? false
)

// Both: the tenant has an optimizer at all, and it is the async one rather than the legacy dialog.
export const selectIsAsyncOptimizerEnabled = createSelector(
  selectIsOptimizeStopsEnabled,
  selectIsAsyncRouteOptimizationEnabled,
  (isOptimizeStopsEnabled, isAsyncRouteOptimizationEnabled) => isOptimizeStopsEnabled && isAsyncRouteOptimizationEnabled
)
